import {
  DiscourseDiscussionPanel,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Button,
  useAgentListening,
  useCreateDiscourseSession,
  useDiscourseSession,
  useDiscourseSupportThread,
  setDiscourseListeningAgentContext,
  navigateWithThreadPreservation,
} from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'


/**
 * Lumie patient consult: auto-play tape; the agent coaches in the bound thread.
 */
export function PatientConsultStubPage() {
  const { sessionId } = useParams<{ sessionId?: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createSession = useCreateDiscourseSession()
  const [bootError, setBootError] = useState<string | null>(null)
  const { setListening, clearListening } = useAgentListening()
  const supportThreadId = useDiscourseSupportThread(sessionId)
  const sessionQuery = useDiscourseSession(sessionId)
  const sessionEnded = (sessionQuery.data?.status || '').toLowerCase() === 'completed'

  useEffect(() => {
    if (sessionId || createSession.isPending || createSession.isSuccess) return
    createSession.mutate(
      {
        kind: 'meeting',
        modality: 'text',
        title: 'Consult assist',
        classifier_profile_id: 'consult_patient_assist',
        demo_mode: true,
        demo_scenario_hint:
          'Patient consult assist demo. Patient speaks in their own words; practitioner stays non-diagnostic.',
      },
      {
        onSuccess: (session) => {
          navigate(`/patient/consult/${session.id}${window.location.search}`, {
            replace: true,
          })
        },
        onError: () => setBootError('Could not create consult session.'),
      },
    )
  }, [sessionId, createSession, navigate])

  useEffect(() => {
    if (!sessionId) {
      clearListening()
      setDiscourseListeningAgentContext(null, null)
      return
    }
    setListening({
      sessionId,
      profileId: 'consult_patient_assist',
      capability: 'record_reminders',
      modeLabel: 'Consult support',
      supportThreadId,
      ended: sessionEnded,
    })
    setDiscourseListeningAgentContext(sessionId, 'consult_patient_assist')
    return () => {
      clearListening()
      setDiscourseListeningAgentContext(null, null)
    }
  }, [sessionId, supportThreadId, sessionEnded, setListening, clearListening])

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Consult assist"
        description="Auto-play patient ↔ practitioner discussion. Coaching arrives in the agent thread (no diagnostic language)."
      />
      {bootError ? (
        <p className="text-sm text-destructive">{bootError}</p>
      ) : !sessionId ? (
        <p className="text-sm text-muted-foreground">Starting session…</p>
      ) : (
        <div className="space-y-4">
          <Button
            type="button"
            variant="ghostMuted"
            size="sm"
            onClick={() =>
              navigateWithThreadPreservation(navigate, '/patient', searchParams)
            }
          >
            Back
          </Button>
          <DiscourseDiscussionPanel
            sessionId={sessionId}
            profileId="consult_patient_assist"
            protagonistRole="patient"
            counterpartRole="practitioner"
            title="Consult discussion"
            subtitle="Simulated patient ↔ practitioner. Press Play to auto-play."
          />
        </div>
      )}
    </PageContainer>
  )
}
